import Avvir from "avvir";

import PlannedBuildingElement from "../../models/domain/planned_building_element";

import type { ApiPlannedElement } from "avvir";
import type { UserSessionStore } from "../../services/reducers/reduce_user_session";

// An element carries `builtStatus` only while it came from the minimal payload — every full ingest clears it.
const isMinimalElement = (element: PlannedBuildingElement) => element?.builtStatus != null;

/**
 * Swaps minimal-payload elements for their full records.
 *
 * The viewer PATCH endpoint writes every column it receives with no fallback to the stored row, so
 * sending a minimal element would blank the element's name, type and classification. The query
 * endpoints are floor-scoped, so this works on areas with no scan dataset too.
 */
const ensureFullPlannedElements = async (projectId: string, floorId: string, elements: PlannedBuildingElement[], user: UserSessionStore): Promise<PlannedBuildingElement[]> => {
  const minimalGlobalIds = elements.filter(isMinimalElement).map(element => element.globalId);
  if (!minimalGlobalIds.length) {
    return elements;
  }

  const query = await Avvir.api.elements.createPlannedBuildingElementsQuery({ projectId, floorId }, minimalGlobalIds, user);
  const fullElements = await Avvir.api.elements.getPlannedBuildingElementsQueryResult({ projectId, floorId }, query.id, user);
  const fullElementsByGlobalId = new Map<string, ApiPlannedElement>();
  for (const fullElement of fullElements) {
    fullElementsByGlobalId.set(fullElement.globalId, fullElement);
  }

  return elements
    .map(element => {
      if (!isMinimalElement(element)) {
        return element;
      }

      const fullElement = fullElementsByGlobalId.get(element.globalId);
      // Not returned by the query means it no longer exists on the floor; drop it rather than send a husk.
      return fullElement ? new PlannedBuildingElement(fullElement) : null;
    })
    .filter(element => element);
};

export default ensureFullPlannedElements;
